import { err, ok, type Result } from 'neverthrow';
import type { EdgeId, EdgeInstance, Graph, NodeId } from '../../../types/graph';
import type { MakeOptional } from '@/types';
import {
  CommandErrorKind,
  CommandKind,
  type CommandResult,
  type CommandError,
  type GraphCommand,
} from '../types';
import { bumpTopology, defaultIdFactory, getTouchingNodeIds } from '../utils';

const hasParentConflict = (
  graph: Readonly<Graph>,
  childId: NodeId,
  edgeId: string
) => {
  return Object.values(graph.edges).some(
    (edge) => edge.from.nodeId === childId && edge.id !== edgeId
  );
};

const createsCycle = (
  graph: Readonly<Graph>,
  childId: NodeId,
  parentId: NodeId,
  edgeId: string
) => {
  const visited = new Set<NodeId>();
  const stack: NodeId[] = [parentId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === childId) {
      return true;
    }
    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    Object.values(graph.edges).forEach((edge) => {
      if (edge.id === edgeId) return;
      if (edge.from.nodeId === current) {
        stack.push(edge.to.nodeId);
      }
    });
  }

  return false;
};

export class AddEdgeCommand implements GraphCommand {
  readonly kind = CommandKind.AddEdge;

  constructor(public readonly edge: MakeOptional<EdgeInstance, 'id' | 'order'>) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    let id = this.edge.id;
    if (!id) {
      id = defaultIdFactory.edgeId();
    }
    const portEdges = Object.values(graph.edges).filter(
      (e) => e.to.nodeId === this.edge.to.nodeId && e.to.portId === this.edge.to.portId
    );
    const maxOrder = portEdges.length > 0
      ? Math.max(...portEdges.map((e) => e.order ?? 0))
      : -1;
    const nextEdge: EdgeInstance = {
      ...this.edge,
      id,
      order: this.edge.order ?? maxOrder + 1,
    };
    const childId = nextEdge.from.nodeId;
    const parentId = nextEdge.to.nodeId;

    if (!graph.nodes[childId]) {
      return err({
        kind: CommandErrorKind.NotFound,
        message: `Node ${childId} not found`,
      });
    }
    if (!graph.nodes[parentId]) {
      return err({
        kind: CommandErrorKind.NotFound,
        message: `Node ${parentId} not found`,
      });
    }
    if (childId === parentId) {
      return err({
        kind: CommandErrorKind.Validation,
        message: 'Cannot connect a node to itself',
      });
    }
    if (hasParentConflict(graph, childId, id)) {
      return err({
        kind: CommandErrorKind.Validation,
        message: `Node ${childId} already has a parent`,
      });
    }
    if (createsCycle(graph, childId, parentId, id)) {
      return err({
        kind: CommandErrorKind.Validation,
        message: 'Edge introduces a cycle, graph must remain a tree',
      });
    }

    const nextGraph: Graph = {
      ...graph,
      edges: {
        ...graph.edges,
        [id]: nextEdge,
      },
    };
    const dirtyEdges = [id];
    const dirtyNodes = getTouchingNodeIds(nextGraph, id);
    const inverse = new RemoveEdgeCommand(id);

    return ok({
      graph: bumpTopology(nextGraph),
      inverse,
      dirty: {
        nodes: dirtyNodes,
        edges: dirtyEdges,
        topologyChanged: true,
      },
    });
  }
}

export class RemoveEdgeCommand implements GraphCommand {
  readonly kind = CommandKind.RemoveEdge;

  constructor(public readonly id: EdgeId) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    const dirtyNodes = getTouchingNodeIds(graph, this.id);
    const removedEdge = graph.edges[this.id];
    const inverse = new AddEdgeCommand(removedEdge);
    const nextEdges = { ...graph.edges };
    delete nextEdges[this.id];
    const nextGraph: Graph = {
      ...graph,
      edges: nextEdges,
    };

    return ok({
      graph: bumpTopology(nextGraph),
      inverse,
      dirty: {
        nodes: dirtyNodes,
        edges: [],
        topologyChanged: true,
      },
    });
  }
}
